/**
 * Web-side widget spec extraction.
 *
 * Mirrors `@archlang/engine`'s extractWidgetSpec for the JSON-serialised
 * shape the server sends to the browser. The server unwraps Values to
 * `{kind, text/value}` records or primitives — this helper normalises
 * both forms.
 *
 * A widget spec is `{ tag, props }`. `tag` is the custom-element name
 * (`widget` field). `props` is every `widget.<...>` field, with the
 * remainder of the path joined by `.` and the value coerced to a JS
 * scalar suitable for serialisation as an HTML attribute.
 */
import type { FieldEntry, Module, Surface, Interface } from "./model.js";
import { valueToString } from "./utils.js";
import { paletteColor } from "../palette.js";

export type WidgetPropValue = string | number | boolean | readonly WidgetPropValue[];

export interface WidgetSpec {
  readonly tag?: string;
  /** Inline-HTML template fallback when no `tag` is configured. */
  readonly template?: string;
  readonly props: ReadonlyMap<string, WidgetPropValue>;
}

const EMPTY: ReadonlyMap<string, WidgetPropValue> = new Map();

export const NO_WIDGET: WidgetSpec = { tag: undefined, template: undefined, props: EMPTY };

export function extractWidgetSpec(
  source: Module | Surface | Interface | { fields: readonly FieldEntry[] }
): WidgetSpec {
  let tag: string | undefined;
  let template: string | undefined;
  let props: Map<string, WidgetPropValue> | undefined;

  for (const f of source.fields) {
    if (f.path.length === 0 || f.path[0] !== "widget") continue;

    if (f.path.length === 1) {
      // The bare `widget` field. Discriminate on Value.kind:
      //   identifier  → custom-element tag
      //   string      → inline-HTML template
      // The server serialises Values as `{kind, text}` records; raw
      // primitives (some test paths) are treated as identifiers since
      // they came from string-typed inputs.
      const v = f.value;
      if (typeof v === "string") {
        // Defensive: a primitive string fell through. Treat as tag —
        // tests construct edges this way and we want them to behave
        // like identifiers, not templates.
        tag = v;
      } else if (typeof v === "object" && v !== null) {
        const obj = v as { kind?: string; text?: string };
        if (obj.kind === "identifier" && typeof obj.text === "string") {
          tag = obj.text;
        } else if (obj.kind === "string" && typeof obj.text === "string") {
          template = obj.text;
        } else {
          // Unknown shape — best-effort: stringify and treat as tag
          // (safer fallback; bad input renders nothing rather than
          // accidentally raw-injecting HTML).
          const s = valueToString(v);
          if (s) tag = s;
        }
      }
      continue;
    }

    const propName = f.path.slice(1).join(".");
    const v = coerceProp(f.value);
    if (v === undefined) continue;
    if (!props) props = new Map();
    props.set(propName, v);

    // Mirror of the engine extractor: a prop resolved FROM a getter also
    // publishes the key it read, as `<prop>Key` — a chip renders
    // "vendor: Stripe Inc", and only the provenance knows the word is
    // "vendor". The wire carries it as a minimal `from` (see serialize.ts).
    if (f.from?.kind === "getter") {
      const key = f.from.path[f.from.path.length - 1];
      if (key) props.set(`${propName}Key`, key);
    }
  }

  return { tag, template, props: props ?? EMPTY };
}

/**
 * Apply `{{path}}` substitution to a template string, mirroring core's
 * `applyWidgetTemplate` against the JSON-serialised module shape.
 *
 *   - `{{name}}` → instance name
 *   - `{{typeName}}` (or `{{kind}}` / `{{kindName}}`) → type name
 *   - `{{description}}` → description
 *   - `{{aspects.<...>}}` → aspect by dotted key
 *   - `{{<...>}}` → field by dotted path (excluding the `widget.*` namespace)
 *
 * Double braces escape HTML; triple braces (`{{{x}}}`) inject raw HTML.
 * Unknown paths render empty.
 */
export function applyWidgetTemplate(
  template: string,
  ctx: { name: string; typeName?: string; description?: string } & {
    fields: readonly FieldEntry[];
    aspects?: readonly { path: readonly string[]; value: unknown }[];
  }
): string {
  const tripleBrace = /\{\{\{\s*([^}\s][^}]*?)\s*\}\}\}/g;
  const doubleBrace = /\{\{\s*([^}\s][^}]*?)\s*\}\}/g;
  const lookup = (token: string): string | undefined => {
    const t = token.trim();
    if (t === "name") return ctx.name;
    if (t === "typeName" || t === "kind" || t === "kindName") return ctx.typeName;
    if (t === "description") return ctx.description;

    const segs = t.split(".");
    if (segs[0] === "aspects" && ctx.aspects) {
      const path = segs.slice(1);
      for (const l of ctx.aspects) {
        if (path.length === l.path.length && path.every((s, i) => s === l.path[i])) {
          return valueToString(l.value);
        }
      }
      return undefined;
    }
    if (segs[0] === "widget") return undefined;
    for (const f of ctx.fields) {
      if (segs.length === f.path.length && segs.every((s, i) => s === f.path[i])) {
        return valueToString(f.value);
      }
    }
    return undefined;
  };

  return template
    .replace(tripleBrace, (_, p: string) => lookup(p) ?? "")
    .replace(doubleBrace, (_, p: string) => escapeHtml(lookup(p) ?? ""));
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// =============================================================================
// Edge style aggregation (interface widget config → edge appearance)
// =============================================================================

/**
 * Visual style for an edge derived from its target interfaces' widget
 * config. Mirrors the renderer's `EdgeWidgetStyle` shape; we keep two
 * declarations because the renderer types live with the board types
 * (no widget package) and this helper sits in the widget module.
 */
export interface EdgeStyle {
  readonly color?: string;
  readonly dashed?: boolean;
  readonly width?: number;
  readonly opacity?: number;
}

/**
 * The full widget-configuration bundle aggregated across the
 * interfaces an edge targets.
 *
 *   style    line-styling derived from `widget.{color,dashed,stroke-width,opacity}`,
 *            applied to the SVG path itself.
 *   tag      custom-element tag from `widget`, mounted as an HTML overlay
 *            at the path midpoint via foreignObject.
 *   template inline-HTML form from `widget.template`, used as a
 *            tag-less fallback when no element is registered.
 *   props    other `widget.<name>` fields (not consumed as style),
 *            forwarded to the overlay element as kebab-cased attributes.
 */
export interface EdgeWidgetAggregate {
  readonly style?: EdgeStyle;
  readonly tag?: string;
  readonly template?: string;
  readonly props?: ReadonlyMap<string, WidgetPropValue>;
}

/** Widget.* prop names that bind to line styling, not the overlay element. */
const STYLE_PROP_NAMES: ReadonlySet<string> = new Set([
  "color",
  "dashed",
  "stroke-width",
  "opacity",
]);

/**
 * Aggregate widget configs across the interfaces an edge targets.
 *
 * Rules:
 *   - For each prop, if every contributing interface declares the
 *     same value, that value wins.
 *   - If they disagree, the prop is left undefined so the renderer
 *     can fall back to its default.
 *   - Interfaces with no widget config don't constrain anything.
 *
 * The conservative aggregation matters: a single edge between two
 * modules can collapse calls to multiple interfaces (each potentially
 * with its own kind / widget). Picking "first wins" or "majority" would
 * produce confusing visuals when interfaces genuinely differ — a
 * mixed-style edge should look neutral, not lie about its homogeneity.
 */
export function aggregateInterfaceStyle(
  interfaces: readonly { fields: readonly FieldEntry[] }[]
): EdgeStyle | undefined {
  return aggregateInterfaceWidget(interfaces).style;
}

/**
 * Like `aggregateInterfaceStyle` but returns the full
 * `EdgeWidgetAggregate` (style + tag + template + non-style props).
 * Layout uses this; resolveEdgeStyle still uses the narrower wrapper
 * above for backwards compatibility.
 */
export function aggregateInterfaceWidget(
  interfaces: readonly { fields: readonly FieldEntry[] }[]
): EdgeWidgetAggregate {
  if (interfaces.length === 0) return {};
  const specs = interfaces.map(extractWidgetSpec);

  // --- line styling ---
  const style: { -readonly [K in keyof EdgeStyle]: EdgeStyle[K] } = {};
  // §8.3.1: a palette NAME becomes pixels HERE — the single point where an
  // interface's widget colour turns into paint. Edges, ports, connections and
  // the incremental re-style pass all arrive through this function, so they
  // cannot disagree, and an export never ships a bare name (which resvg and
  // browsers would resolve to the CSS colour of that name, not ours).
  const rawColor = sameOr(specs, (s) => s.props.get("color")) as string | undefined;
  style.color = rawColor === undefined ? undefined : paletteColor(rawColor);
  style.dashed = sameOr(specs, (s) => s.props.get("dashed")) as boolean | undefined;
  style.opacity = sameOr(specs, (s) => s.props.get("opacity")) as number | undefined;
  const w = sameOr(specs, (s) => s.props.get("stroke-width"));
  if (typeof w === "number") style.width = w;
  const hasStyle =
    style.color !== undefined ||
    style.dashed !== undefined ||
    style.width !== undefined ||
    style.opacity !== undefined;

  // --- overlay widget tag ---
  const tagAgreed = sameOr(specs, (s) => s.tag);

  // --- inline template ---
  const templateAgreed = sameOr(specs, (s) => s.template);

  // --- non-style overlay props ---
  // Collect the union of all overlay-prop keys across specs, then
  // require unanimous agreement on each value (same as style props).
  // Keys are anything under widget.* that isn't a style prop.
  const propKeys = new Set<string>();
  for (const s of specs) {
    for (const k of s.props.keys()) {
      if (STYLE_PROP_NAMES.has(k)) continue;
      propKeys.add(k);
    }
  }
  let props: Map<string, WidgetPropValue> | undefined;
  for (const k of propKeys) {
    const v = sameOr(specs, (s) => s.props.get(k));
    if (v === undefined) continue;
    if (!props) props = new Map();
    props.set(k, v);
  }

  const out: EdgeWidgetAggregate = {};
  if (hasStyle) (out as { style?: EdgeStyle }).style = style;
  if (typeof tagAgreed === "string") (out as { tag?: string }).tag = tagAgreed;
  if (typeof templateAgreed === "string")
    (out as { template?: string }).template = templateAgreed;
  if (props && props.size > 0)
    (out as { props?: ReadonlyMap<string, WidgetPropValue> }).props = props;
  return out;
}

/**
 * Returns the common value across `specs` according to `pick`, or
 * undefined when any contributor disagrees or omits the prop.
 */
function sameOr<T>(
  specs: readonly WidgetSpec[],
  pick: (s: WidgetSpec) => T | undefined
): T | undefined {
  let resolved: T | undefined;
  for (const s of specs) {
    const v = pick(s);
    if (v === undefined) return undefined;
    if (resolved === undefined) {
      resolved = v;
    } else if (resolved !== v) {
      return undefined;
    }
  }
  return resolved;
}

/**
 * Coerce a serialised value (wrapped Value record or primitive) into a
 * scalar suitable for a widget prop. Returns undefined for empty values.
 */
function coerceProp(v: unknown): WidgetPropValue | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
  if (Array.isArray(v)) {
    const out: WidgetPropValue[] = [];
    for (const item of v) {
      const c = coerceProp(item);
      if (c !== undefined) out.push(c);
    }
    return out;
  }
  if (typeof v === "object") {
    const obj = v as { kind?: string; text?: string; value?: unknown; items?: unknown[] };
    if (obj.kind === "string" || obj.kind === "identifier") return obj.text;
    if (obj.kind === "number" || obj.kind === "boolean") return obj.value as number | boolean;
    if (obj.kind === "list" && Array.isArray(obj.items)) {
      const out: WidgetPropValue[] = [];
      for (const item of obj.items) {
        const c = coerceProp(item);
        if (c !== undefined) out.push(c);
      }
      return out;
    }
    // Fallback: try the generic value-to-string path.
    const s = valueToString(v);
    if (s) return s;
  }
  return undefined;
}

/**
 * Convert a widget prop value to its HTML-attribute form.
 * Lists become JSON. Booleans become "true"/"false". Numbers become
 * their decimal string. Strings pass through unchanged. Renderer
 * consumers feed the result straight into element.setAttribute.
 */
export function propValueToAttr(v: WidgetPropValue): string {
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

/**
 * Convert a prop name (which may contain dots from a nested field path)
 * into its kebab-cased attribute form. `style.border` → `style-border`,
 * `iconColor` → `icon-color`.
 */
export function propNameToAttr(name: string): string {
  return name
    .replace(/\./g, "-")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase();
}

/**
 * The widget props whose value is a COLOUR — the engine's `COLOR_FIELDS` as
 * they arrive here, plus the kebab spellings hand-built prop maps use.
 *
 * A palette name is not pixels until `paletteColor` resolves it, and an
 * unresolved one is not a MISSING colour but a WRONG one: CSS paints its own
 * `crimson` (#dc143c) where the theme means #f43f5e, so the live board and the
 * export disagree about the same source text. Every backend therefore runs
 * these — and only these — through `paletteColor`: the scene composer in
 * `moduleToCardInput`, the live shell where it stamps DOM attributes. Anything
 * not listed is text and must survive verbatim (a card whose subheader reads
 * "crimson" is a word, not paint).
 */
export const COLOR_PROPS: ReadonlySet<string> = new Set([
  "color",
  "accent",
  "bg",
  "badgeColor",
  "badge-color",
]);
